/**
 * Centralized query key factory for TanStack Query.
 *
 * Rules:
 * - Always derive keys from this file (never hand-write strings in components).
 * - Keys are arrays. The first element is the feature, then narrowing args.
 * - Mutations invalidate by the broadest matching key.
 * - Use the `userId ?? "anon"` pattern so logging out clears the cache cleanly
 *   and a different user logging in cannot read the previous user's data.
 *
 * As more features migrate to TanStack Query, add their key namespaces here.
 */

export const queryKeys = {
  auth: {
    all: ["auth"] as const,
    adminRole: (userId: string | undefined) =>
      ["auth", "adminRole", userId ?? "anon"] as const,
    accountStatus: (userId: string | undefined) =>
      ["auth", "accountStatus", userId ?? "anon"] as const,
  },
  admin: {
    all: ["admin"] as const,
    // Originals ops feed for /admin/originals. `window` is the lookback in days
    // and `status` the pipeline filter, so switching either refetches rather
    // than silently serving the previous slice.
    originalsRuns: (windowDays: number, status: string) =>
      ["admin", "originalsRuns", windowDays, status] as const,
    originalsTotals: (windowDays: number) =>
      ["admin", "originalsTotals", windowDays] as const,
  },
  projects: {
    all: ["projects"] as const,
    list: () => ["projects", "list"] as const,
    coverImages: (ids: string[] | undefined) =>
      ["projects", "coverImages", ids ?? []] as const,
  },
  chapters: {
    all: ["chapters"] as const,
    ready: (maxAgeHours: number | undefined) =>
      ["chapters", "ready", maxAgeHours ?? "any"] as const,
  },
  wallet: {
    all: ["wallet"] as const,
    mana: (userId: string | undefined) =>
      ["wallet", "mana", userId ?? "anon"] as const,
    manaTransactions: (userId: string | undefined, limit: number) =>
      ["wallet", "manaTransactions", userId ?? "anon", limit] as const,
    sonic: (userId: string | undefined) =>
      ["wallet", "sonic", userId ?? "anon"] as const,
    ink: (userId: string | undefined) =>
      ["wallet", "ink", userId ?? "anon"] as const,
    /** Net Ink one Originals chapter has consumed (ledger-derived). */
    chapterInk: (userId: string | undefined, chapterId: string | undefined) =>
      ["wallet", "chapterInk", userId ?? "anon", chapterId ?? "none"] as const,
    starterFlag: (userId: string | undefined) =>
      ["wallet", "starterFlag", userId ?? "anon"] as const,
    apologyGift: (userId: string | undefined, reference: string) =>
      ["wallet", "apologyGift", userId ?? "anon", reference] as const,
  },
  subscription: {
    all: ["subscription"] as const,
    status: (userId: string | undefined) =>
      ["subscription", "status", userId ?? "anon"] as const,
  },
  usage: {
    all: ["usage"] as const,
    chapters: (userId: string | undefined) =>
      ["usage", "chapters", userId ?? "anon"] as const,
  },
  dashboard: {
    profile: (userId: string | undefined) =>
      ["dashboard", "profile", userId ?? "anon"] as const,
  },
  voice: {
    projects: ["voice", "projects"] as const,
  },
} as const;
